import { Input } from "./input";
import { Button } from "./button";
import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { 
  Search, UserPlus, Loader2, AlertCircle, 
  Check, Users, UserCheck, Info
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { motion, AnimatePresence } from "framer-motion";

// Extended User type that includes profile
interface UserWithProfile {
  id: number;
  username: string;
  email?: string | null;
  role?: string;
  createdAt?: Date;
  updatedAt?: Date;
  profile?: {
    avatarId?: number | null;
    avatarSource?: string;
    status?: string;
    level?: number;
  };
}

export function FriendSearch() {
  const [username, setUsername] = useState("");
  const [searchStatus, setSearchStatus] = useState<"idle" | "searching" | "success" | "error">("idle");
  const [foundUser, setFoundUser] = useState<UserWithProfile | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const { toast } = useToast();

  // Query to fetch avatar data when a user is found
  const { data: avatarData } = useQuery({
    queryKey: ["search-avatar", foundUser?.profile?.avatarId, foundUser?.profile?.avatarSource],
    queryFn: async () => {
      if (!foundUser?.profile?.avatarId) return null;
      
      const endpoint = `/api/customization/avatars/${foundUser.profile.avatarId}`;
        
      const res = await apiRequest("GET", endpoint);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!foundUser?.profile?.avatarId,
  });

  const searchMutation = useMutation({
    mutationFn: async (username: string) => {
      setSearchStatus("searching");
      const res = await apiRequest("GET", `/api/friends/search?username=${username.trim()}`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "User not found");
      }
      return res.json();
    },
    onSuccess: (data: UserWithProfile) => {
      setFoundUser(data);
      setSearchStatus("success");
    },
    onError: (error: Error) => {
      setSearchStatus("error");
      setErrorMessage(error.message || "No user found with that username");
      toast({
        title: "User not found",
        description: error.message || "No user found with that username",
        variant: "destructive",
      });
    },
  });

  const sendFriendRequest = useMutation({
    mutationFn: async (friendId: number) => {
      const res = await apiRequest("POST", "/api/friends/request", { friendId });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to send friend request");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Friend request sent",
        description: `A request has been sent to ${foundUser?.username}`,
      });
      setUsername("");
      setFoundUser(null);
      setSearchStatus("idle");
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send request",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    }
  });

  // Reset search results when username changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (searchStatus !== "idle") {
      setSearchStatus("idle");
      setFoundUser(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={username}
            onChange={handleInputChange}
            placeholder="Search by username..."
            className="pl-10 py-6 bg-black/40 border-white/10 focus:border-primary/50 rounded-lg w-full text-white"
          />
        </div>
        
        <Button
          onClick={() => searchMutation.mutate(username)}
          disabled={!username || searchMutation.isPending}
          className="h-10 mt-3 w-full bg-primary/80 hover:bg-primary text-black font-medium rounded-lg"
        >
          {searchMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Searching...
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4 mr-2" />
              Find Friend
            </>
          )}
        </Button>
      </div>

      <AnimatePresence>
        {searchStatus === "error" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Alert variant="destructive" className="bg-red-950/30 border-red-900/50 text-white">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>User not found</AlertTitle>
              <AlertDescription className="text-white/80">
                {errorMessage}
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        {searchStatus === "success" && foundUser && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-black/60 backdrop-blur-md rounded-lg border border-white/10 p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 ring-2 ring-black/50">
                  {avatarData?.imageUrl ? (
                    <AvatarImage src={avatarData.imageUrl} className="object-cover" />
                  ) : (
                    <AvatarFallback className="bg-black text-primary font-bold">
                      {foundUser.username.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h3 className="text-base font-medium text-white">
                    {foundUser.username}
                  </h3>
                  {foundUser.profile?.status && (
                    <div className="text-xs text-white/50">
                      {foundUser.profile.status}
                    </div>
                  )}
                </div>
              </div>
              
              <Button
                onClick={() => sendFriendRequest.mutate(foundUser.id)}
                disabled={sendFriendRequest.isPending}
                size="sm"
                className="h-9 px-4 bg-primary/80 hover:bg-primary text-black rounded-full"
              >
                {sendFriendRequest.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <UserCheck className="w-3.5 h-3.5 mr-1.5" />
                    <span className="text-xs font-medium">Send Request</span>
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {searchStatus === "idle" && !foundUser && (
        <div className="text-center py-4 text-white/50 space-y-2">
          <Users className="w-8 h-8 mx-auto opacity-30" />
          <p className="text-sm">Enter a username to find friends</p>
        </div>
      )}
      
      <div className="mt-4 border-t border-white/5 pt-4">
        <div className="flex items-start gap-2 text-xs text-white/40">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <p>
            You can only send friend requests to players who have public profiles. 
            Friend requests need to be accepted before you can play together.
          </p>
        </div>
      </div>
    </div>
  );
}
